import type { Dispatch, ReactNode, SetStateAction } from "react";

import type { IWizardLegendItemSettings } from "@similarweb/ui-components";

import type { ISearchEngineDetails } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordAndTags,
    IKeywordsCampaignsWizardCompetitor,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { StandardGscDraft } from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/types";
import type { IImportedKeywords, IManualKeywords } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import type {
    CampaignsFields,
    LOCATION_SORT_ORDER,
} from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import type { WizardMode } from "app/pages/digital-marketing/keywords-campaigns/wizard/types";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

export interface ICampaignsWizardNavButtonsProps {
    wizardMode: WizardMode | null;
    isNextHidden: boolean;
    isNextDisabled: boolean;
    isLoading?: boolean;
    onBackStepClick: VoidFunction;
    onNextStepClick: VoidFunction;
    isLastStep: boolean;
    tooltipContent?: string;
}

export enum ECampaignPagesError {
    /* eslint-disable @typescript-eslint/naming-convention */
    EMPTY_OR_DUPLICATE_SITE_STEP_ERROR = "siteStepError",
    KEYWORDS_LIMIT_EXCEEDED = "keywordsLimitExceeded",
    EMPTY_TEXT_AREA_STEP_ERROR = "textAreaStepError",
    SEARCH_ENGINE_HAS_NOT_KEYWORDS = "searchEngineHasNotKeywords",
    NO_DATA = "noData",
    MAX_LENGTH = "maxLength",
    MINIMUM_LENGTH = "minimumLength",
    DUPLICATE_NAME = "duplicateName",
    EMPTY_FIELD = "emptyField",
    INVALID_TEXT = "invalidText",
    SITE_MAX_LENGTH = "siteMaxLength",
    INVALID_CAMPAIGN_NAME = "invalidName",
    INVALID_CAMPAIGN_NAME_BOUNDARIES = "invalidNameBoundaries",
    /* eslint-enable @typescript-eslint/naming-convention */
}

export type CampaignsPagesError = ECampaignPagesError | null;

type CampaignField<T = unknown> = {
    value: T;
    hasError: boolean;
};

export type ICampaignsFieldsCollection = {
    [key: string]: CampaignField;
};

type ICampaignsWizardStepProps<T extends ICampaignsFieldsCollection> = {
    fields: T;
    legendData: IWizardLegendItemSettings;
    pageError?: CampaignsPagesError;
    shouldShowKeywordCounter: boolean;
};

export type IYourSiteStep = ICampaignsWizardStepProps<{
    selectedSite: CampaignField<{
        site: string;
        metadata: IWebsiteItem;
    } | null>;
    entities: CampaignField<string[]>;
    competitors: CampaignField<IKeywordsCampaignsWizardCompetitor[]>;
}>;

export type ISearchEnginesStep = ICampaignsWizardStepProps<{
    searchEngines: CampaignField<ISearchEngineDetails[]>;
    lastSearchEngineID: CampaignField<number>;
}>;

export type IKeywordsStep = ICampaignsWizardStepProps<{
    keywordSources: CampaignField<[IManualKeywords, ...IImportedKeywords[]]>;
    standardGscDraft: CampaignField<StandardGscDraft | null>;
}>;

export type IReviewStep = ICampaignsWizardStepProps<{
    projectName: CampaignField<string | null>;
    isSubscribedToEmail: CampaignField<boolean>;
    keywordsAndTags: CampaignField<IKeywordAndTags[]>;
}>;

export type ICampaignsWizardState = {
    pages: [IYourSiteStep, ISearchEnginesStep, IKeywordsStep, IReviewStep];
    wizardHasError?: boolean;
    campaignsFields: typeof CampaignsFields;
};

export type ICampaignsContext = {
    children: ReactNode;
    defaultState: ICampaignsWizardState;
    amountOfKeywordsRemaining: number;
};

export interface ISupportedLocationByGoogle {
    countryCode?: string;
    canonicalName?: string;
    type?: (typeof LOCATION_SORT_ORDER)[number];
    name: string;
}
export interface ISupportedLanguageByGoogle {
    locale: {
        [local: string]: {
            description: string;
            domain: string;
        };
    };
}
export interface ISupportedLanguagesAndLocale {
    locale: string;
    description: string;
}

export interface ICampaignsWizardContext {
    wizardState: ICampaignsWizardState;
    setWizardState: Dispatch<SetStateAction<ICampaignsWizardState>>;
    activePageIndex: 0 | 1 | 2 | 3;
    amountOfKeywordsRemaining: number;
    setActivePageIndex: Dispatch<SetStateAction<0 | 1 | 2 | 3>>;
    defaultState: ICampaignsWizardState;
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics;
}

/* stands for all the countries that contains the forbidden location */
